// Assisted drafting for one section, on real backend runs: inputs handed to
// the agent, the draft with clickable citations, the evidence behind each
// statement, checks, and the run history. Nothing here invents content: a
// section the backend does not support says so.
import { Fragment, useEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Copy, ExternalLink, Mail, MessageSquare, PanelRight, Pencil, Plus, Rows3, Send, X } from "lucide-react";
import { api } from "../api/client";
import { invalidate, useMutation, useResource } from "../api/resource";
import type { AuditFindingItem, BlockCitation, BlockResult, ContributorRequestItem, DerivedFigureItem, FactItem, FigureTableData, Generation, ReviewStatus, SectionContent,
  SFCRTable, SFCRTableColumn, SFCRTableRow,
  SectionReviewState } from "../api/types";
import { useCurrentUser } from "../auth";
import { keys, useDossier } from "../dossier";
import { formatDateTime, w } from "../i18n";
import type { UserStatus } from "../status";
import { Dialog, SidePanel } from "./overlay";
import { EmptyState, ErrorState, Notice, SkeletonBlock, StatusBadge } from "./ui";

const ACTIVE = ["queued", "running"];

export function indicatorLabel(indicator: string): string {
  if (indicator.startsWith("variation_")) return w("ind.variation", { label: indicatorLabel(indicator.slice(10)) });
  const label = w(`ind.${indicator}`);
  return label === `ind.${indicator}` ? indicator : label;
}

export function formatAmount(value: number, unit: string | null | undefined): string {
  const locale = w("locale.number");
  if (unit === "%" || unit === "pts") return `${value.toLocaleString(locale, { maximumFractionDigits: 1 })} ${unit === "%" ? "%" : "pts"}`;
  return `${value.toLocaleString(locale, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} ${unit ?? ""}`.trim();
}

/** "322 M€" for 322 084,02 k€: amounts compared at a glance. The exact value
 * stays one hover away (`formatAmount`). */
export function formatCompact(value: number, unit: string | null | undefined): string {
  if (unit !== "k€") return formatAmount(value, unit);
  const magnitude = Math.abs(value);
  const [scaled, suffix] = magnitude >= 1e6 ? [value / 1e6, w("unit.bn")] : magnitude >= 1e3 ? [value / 1e3, w("unit.m")]
    : [value, "k€"];
  return `${scaled.toLocaleString(w("locale.number"), { maximumFractionDigits: Math.abs(scaled) >= 100 ? 0 : 1 })} ${suffix}`;
}

export function formatRunDuration(ms: number | null | undefined): string | null {
  if (ms == null || ms < 0) return null;
  const total = Math.max(0, Math.round(ms / 1000));
  if (total < 60) return `${total} s`;
  const minutes = Math.floor(total / 60), seconds = total % 60;
  return seconds ? `${minutes} min ${seconds} s` : `${minutes} min`;
}

function elapsedMs(startedAt?: string | null, fallbackAt?: string | null, now = Date.now()): number | null {
  const start = Date.parse(startedAt || fallbackAt || "");
  return Number.isFinite(start) ? Math.max(0, now - start) : null;
}

function useNow(enabled: boolean): number {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!enabled) return;
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [enabled]);
  return now;
}

export function runDuration(generation: Pick<Generation, "duration_ms" | "started_at" | "created_at" | "status">, now = Date.now()): string | null {
  if (generation.duration_ms != null) return formatRunDuration(generation.duration_ms);
  if (!ACTIVE.includes(generation.status)) return null;
  return formatRunDuration(elapsedMs(generation.started_at, generation.created_at, now));
}

/** N against N-1 in one figure: % for amounts, points for ratios; up in green, down in red. */
export function Variation({ delta, deltaUnit, variation }: { delta: number | null; deltaUnit: string | null;
                                                            variation: number | null }) {
  const points = deltaUnit === "pts" || deltaUnit === "%";
  const value = points ? delta : variation;
  if (delta == null || value == null) return <span className="ws-faint">—</span>;
  const text = `${value > 0 ? "+" : ""}${value.toLocaleString(w("locale.number"), { maximumFractionDigits: 1 })} ${points ? "pts" : "%"}`;
  return <span className={value > 0 ? "ws-up" : value < 0 ? "ws-down" : undefined}
    title={`${delta > 0 ? "+" : ""}${formatAmount(delta, deltaUnit)}`}>{text}</span>;
}

const CONFIDENCE_STATUS: Record<FactItem["confidence"], UserStatus> = { eleve: "ready", moyen: "partial", faible: "needs_review" };

function ConfidenceBadge({ confidence }: { confidence: FactItem["confidence"] }) {
  return <StatusBadge status={CONFIDENCE_STATUS[confidence]} label={w(`confidence.${confidence}`)} />;
}

export function blockUserStatus(generation: Generation | undefined): UserStatus {
  if (!generation) return "not_started";
  if (ACTIVE.includes(generation.status)) return "processing";
  if (generation.status === "failed") return "failed";
  return ({ produced: "generated", audit_ko: "needs_review", escalated: "missing", validated: "approved" } as const)[
    generation.block_status as "produced"] ?? "not_started";
}

/** A queued or running run stores an empty result; only a complete block state counts. */
export function completedResult(generation: Generation | undefined): BlockResult | undefined {
  const result = generation?.result;
  return result && Array.isArray(result.citations) && Array.isArray(result.gaps) ? result : undefined;
}

export function useGenerations(code: string) {
  const { dossier } = useDossier();
  const [polling, setPolling] = useState(false);
  const generations = useResource(keys.generations(dossier.id, code), () => api.generations(dossier.id, code),
    { pollMs: polling ? 2000 : undefined });
  const running = !!generations.data?.items[0] && ACTIVE.includes(generations.data.items[0].status);
  const wasRunning = useRef(false);
  useEffect(() => {
    setPolling(running);
    if (wasRunning.current && !running) invalidate(keys.generationSummary(dossier.id), keys.audit(dossier.id), `report-audits:${dossier.id}`,
      keys.review(dossier.id, code), keys.reviews(dossier.id), `report:${dossier.id}`, `comparison:${dossier.id}`, `schedule:${dossier.id}`);
    wasRunning.current = running;
  }, [running, dossier.id]);
  return generations;
}

/** Facts the agent receives, with where each comes from. */
export function AgentInputs({ code }: { code: string }) {
  const { dossier, inspect } = useDossier();
  const inputs = useResource(keys.inputs(dossier.id, code), () => api.sectionInputs(dossier.id, code));
  if (inputs.loading) return <SkeletonBlock lines={4} />;
  if (inputs.error?.code === "SECTION_NOT_SUPPORTED") return null;
  if (inputs.error) return <ErrorState title={w("draft.inputsError")} error={inputs.error} onRetry={inputs.reload} />;
  const data = inputs.data!;
  const facts = Object.entries(data.facts);
  return <section className="ws-card" style={{ marginTop: 16 }}>
    <div className="ws-card-head"><h2>{w("draft.agentInputs")}</h2>
      <span className="ws-small ws-muted">{w(`draft.kind.${data.kind}`)}</span>
      <StatusBadge status={data.can_draft ? "ready" : "missing"} label={data.can_draft ? w("draft.canDraft") : w("draft.cannotDraft")} /></div>
    {facts.length > 0 && <div className="ws-table-wrap"><table className="ws-table">
      <thead><tr><th>{w("draft.col.indicator")}</th><th>{w("draft.col.year")}</th><th className="num">{w("draft.col.value")}</th>
        <th>{w("draft.col.source")}</th><th>{w("draft.col.confidence")}</th></tr></thead>
      <tbody>{facts.map(([key, fact]) => <tr key={key}>
        <td>{indicatorLabel(fact.indicator)}</td><td>{fact.year}</td>
        <td className="num">{formatAmount(fact.value, fact.unit)}<span className="ws-cell-sub">{fact.raw_value}</span></td>
        <td><button className="ws-link" onClick={() => inspect(fact.source_doc_id)}>{fact.location}</button>
          {fact.extraction_method === "qrt_identity" && <span className="ws-cell-sub">{w("draft.derivedFromCells")}</span>}</td>
        <td><ConfidenceBadge confidence={fact.confidence} /></td>
      </tr>)}</tbody></table></div>}
    {data.passages.length > 0 && <div className="ws-card-pad"><h3 className="ws-subtitle">{w("draft.passages")}</h3>
      <ul className="ws-list ws-card">{data.passages.map(passage => <li key={passage.id} style={{ alignItems: "flex-start" }}>
        <span className="ws-list-title"><strong>{passage.title}</strong>
          <span className="ws-cell-sub">{passage.document_name}{passage.location ? ` · ${passage.location}` : ""}</span>
          <span className="ws-cell-sub">{passage.excerpt}</span></span>
        {passage.period_role && <span className="ws-tag">{passage.period_role}</span>}
      </li>)}</ul></div>}
    {data.missing.length > 0 && <div className="ws-card-pad"><h3 className="ws-subtitle">{w("draft.missingInputs")}</h3>
      <ul className="ws-small" style={{ margin: 0 }}>{data.missing.map(key => {
        const [indicator, year] = key.split("@");
        return <li key={key}>{indicatorLabel(indicator)} ({year}){data.required.includes(key) ? ` · ${w("ready.required")}` : ""}</li>;
      })}</ul></div>}
    {data.notes.length > 0 && <div className="ws-card-pad" style={{ paddingTop: 0 }}><ul className="ws-small ws-muted" style={{ margin: 0 }}>
      {data.notes.map(note => <li key={note}>{note}</li>)}</ul></div>}
  </section>;
}

interface Selection { text: string; refs: number[] }

/** Renders the draft's light markdown (headings, bullets) with [n] markers as buttons. */
export function DraftText({ text, citations, onSelect }: { text: string; citations: BlockCitation[]; onSelect: (s: Selection) => void }) {
  const blocks: ReactNode[] = [];
  let bullets: string[] = [];
  const refsOf = (line: string) => [...line.matchAll(/\[(\d+)\]/g)].map(m => Number(m[1])).filter(n => n >= 1 && n <= citations.length);
  const inline = (line: string) => line.split(/(\[\d+\])/g).map((part, index) => {
    const match = part.match(/^\[(\d+)\]$/);
    if (!match) return <Fragment key={index}>{part}</Fragment>;
    const n = Number(match[1]);
    return <button key={index} className="ws-cite" aria-label={w("draft.citation", { n })}
      onClick={event => { event.stopPropagation(); onSelect({ text: line.replace(/\[\d+\]/g, "").trim(), refs: [n] }); }}>{n}</button>;
  });
  const statement = (line: string, key: string, Tag: "p" | "li") => {
    const refs = refsOf(line);
    return <Tag key={key} className={refs.length ? "ws-statement" : undefined} tabIndex={refs.length ? 0 : undefined}
      onClick={refs.length ? () => onSelect({ text: line.replace(/\[\d+\]/g, "").trim(), refs }) : undefined}
      onKeyDown={refs.length ? event => { if (event.key === "Enter") onSelect({ text: line.replace(/\[\d+\]/g, "").trim(), refs }); } : undefined}>
      {inline(line)}</Tag>;
  };
  const flush = (key: string) => {
    if (bullets.length) blocks.push(<ul key={key}>{bullets.map((line, i) => statement(line, `${key}-${i}`, "li"))}</ul>);
    bullets = [];
  };
  // A draft written on the N-1 report layout: tables the server filled ([[TABLEAU ...]] then "| a | b |" rows),
  // N-1 pictures kept as they are ([[FIGURE ...]]), bold run-in subheadings (**...**).
  let table: { key: string; rows: string[][] } | null = null;
  const closeTable = () => {
    if (table) blocks.push(<table key={table.key} className="ws-draft-table"><tbody>{table.rows.map((row, r) =>
      <tr key={r}>{row.map((cell, c) => <td key={c}>{cell}</td>)}</tr>)}</tbody></table>);
    table = null;
  };
  text.split(/\n/).forEach((raw, index) => {
    const line = raw.trim();
    if (table) {
      if (line.startsWith("|")) { table.rows.push(line.replace(/^\||\|$/g, "").split("|").map(cell => cell.trim())); return; }
      closeTable();
      if (line === "[[/TABLEAU]]" || !line) return;
    }
    const marker = line.match(/^\[\[(TABLEAU|FIGURE)\s+([^\]]+)\]\]$/);
    if (marker) {
      flush(`b${index}`);
      if (marker[1] === "TABLEAU") table = { key: `t${index}`, rows: [] };
      else blocks.push(<p key={index} className="ws-draft-figure">{w("draft.figure", { id: marker[2] })}</p>);
      return;
    }
    const bold = line.match(/^\*\*(.+?)\*\*\s*((?:\[\d+\]\s*)*)$/);
    if (/^[-*•]\s+/.test(line) && !bold) { bullets.push(line.replace(/^[-*•]\s+/, "").replace(/\*\*/g, "")); return; }
    flush(`b${index}`);
    if (!line) return;
    const heading = line.match(/^(#{1,6})\s+(.*)$/);
    if (heading) {
      const Tag = heading[1].length > 3 ? "h4" : "h3";
      blocks.push(<Tag key={index} className="ws-draft-heading">{heading[2]}</Tag>);
      return;
    }
    if (bold) { blocks.push(<div key={index} className="ws-draft-subheading">{statement(`${bold[1]} ${bold[2]}`.trim(), `s${index}`, "p")}</div>); return; }
    blocks.push(statement(line.replace(/\*\*/g, ""), `p${index}`, "p"));
  });
  closeTable();
  flush("end");
  return <div className="ws-draft">{blocks}</div>;
}

function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function HighlightedDataPoint({ text, fact }: { text: string; fact?: FactItem }) {
  const candidates = [fact?.raw_value, fact ? formatAmount(fact.value, fact.unit) : null]
    .filter((value): value is string => !!value)
    .map(value => value.replace(/\s*\(.+\)\s*$/, "").trim())
    .filter(Boolean);
  for (const value of candidates) {
    const pattern = value.split(/\s+/).map(escapeRegex).join("\\s+");
    const match = pattern ? new RegExp(pattern, "i").exec(text) : null;
    if (!match) continue;
    const start = match.index, end = start + match[0].length;
    return <>{text.slice(0, start)}<mark className="ws-data-mark">{text.slice(start, end)}</mark>{text.slice(end)}</>;
  }
  return <>{text}</>;
}

function EvidencePanel({ selection, result, facts, onClose, inline = false }: {
  selection: Selection; result: BlockResult; facts: Record<string, FactItem>; onClose: () => void; inline?: boolean;
}) {
  const { documents } = useDossier();
  const name = (id: string) => documents.data?.find(d => d.id === id)?.display_name || id;
  const body = <>
    <section><h3 className="ws-subtitle">{w("evidence.statement")}</h3><blockquote className="ws-quote">{selection.text}</blockquote></section>
    <section><h3 className="ws-subtitle">{w("evidence.sources")}</h3>
      <div className="ws-stack">{selection.refs.map(n => {
        const citation = result.citations[n - 1];
        if (!citation) return null;
        const fact = facts[citation.sourceId];
        return <article key={n} className="ws-card ws-card-pad">
          <div className="ws-row" style={{ justifyContent: "space-between" }}>
            <strong>[{n}] {citation.indicator ? indicatorLabel(citation.indicator) : citation.title}{citation.year ? ` · ${citation.year}` : ""}</strong>
            {fact && <ConfidenceBadge confidence={fact.confidence} />}
            {citation.kind === "passage" && <span className="ws-tag">{w("evidence.passage")}</span>}
          </div>
          {fact && <p style={{ margin: "6px 0" }}><span className="ws-mono">{formatAmount(fact.value, fact.unit)}</span>
            <span className="ws-faint ws-small"> · {fact.raw_value}</span></p>}
          <dl className="ws-dl ws-small">
            <dt>{w("evidence.document")}</dt><dd>{citation.docId ? name(citation.docId) : citation.location}</dd>
            <dt>{w("evidence.location")}</dt><dd>{citation.location || w("common.notAvailable")}{citation.page && !/p\.\s?\d/.test(citation.location || "") ? ` · ${w("common.pages", { pages: citation.page })}` : ""}</dd>
          </dl>
          <pre className="ws-source-text" style={{ marginTop: 8, maxHeight: 180 }}><HighlightedDataPoint text={citation.quote} fact={fact} /></pre>
          {citation.docId && <a className="ws-btn sm" href={api.fileUrl(citation.docId, citation.page)} target="_blank" rel="noreferrer"><ExternalLink size={14} />{w("evidence.openSource")}</a>}
        </article>;
      })}</div></section>
  </>;
  if (!inline) return <SidePanel title={w("evidence.title")} onClose={onClose}>{body}</SidePanel>;
  return <section className="ws-card ws-evidence-inline" aria-label={w("evidence.title")}>
    <div className="ws-card-head"><h2>{w("evidence.title")}</h2>
      <button className="ws-btn sm ghost" onClick={onClose} aria-label={w("common.close")}><X size={14} /></button></div>
    <div className="ws-card-pad ws-stack">{body}</div>
  </section>;
}

/** The checks' findings on the current text, at a glance (details in the Checks tab). */
function FindingsCard({ findings }: { findings: AuditFindingItem[] }) {
  const blocking = findings.filter(f => f.severity === "blocking").length;
  return <section className="ws-card">
    <div className="ws-card-head"><h2>{w("draft.findingsTitle")}</h2>
      {findings.length > 0 && <span className="ws-small ws-muted">{w("draft.findingsCount", { blocking, warnings: findings.length - blocking })}</span>}</div>
    {!findings.length && <p className="ws-card-pad ws-small" style={{ margin: 0 }}><StatusBadge status="ready" label={w("draft.noFindings")} /></p>}
    <ul className="ws-list">{findings.map((finding, index) => <li key={index} style={{ alignItems: "flex-start" }}>
      <span className="ws-small">{finding.message}</span>
      <StatusBadge status={finding.severity === "blocking" ? "failed" : "needs_review"} label={w(`validation.${finding.severity}`)} />
    </li>)}</ul>
  </section>;
}

type Layout = "side" | "stack";
const LAYOUT_KEY = "sfcr.draftLayout";

/** The draft layout: the agent's output in a right sidebar, or top-down. Remembered per browser;
 * a narrow screen always gets top-down. */
function useDraftLayout(): [Layout, (layout: Layout) => void, boolean] {
  const [layout, setLayout] = useState<Layout>(() => {
    try { return window.localStorage.getItem(LAYOUT_KEY) === "stack" ? "stack" : "side"; } catch { return "side"; }
  });
  const query = "(max-width: 900px)";
  const [narrow, setNarrow] = useState(() => typeof window !== "undefined" && window.matchMedia?.(query).matches);
  useEffect(() => {
    const media = window.matchMedia?.(query);
    if (!media) return;
    const update = () => setNarrow(media.matches);
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);
  const choose = (next: Layout) => {
    setLayout(next);
    try { window.localStorage.setItem(LAYOUT_KEY, next); } catch { /* storage unavailable: kept for this page only */ }
  };
  return [narrow ? "stack" : layout, choose, narrow];
}

function DerivedTable({ figures }: { figures: DerivedFigureItem[] }) {
  if (!figures.length) return null;
  return <section className="ws-card" style={{ marginTop: 16 }}>
    <div className="ws-card-head"><h2>{w("draft.derived")}</h2><span className="ws-small ws-muted">{w("draft.derivedHint")}</span></div>
    <div className="ws-table-wrap"><table className="ws-table"><thead><tr><th>{w("draft.col.indicator")}</th><th>{w("draft.col.year")}</th>
      <th className="num">{w("draft.col.value")}</th><th>{w("draft.col.formula")}</th></tr></thead>
      <tbody>{figures.map(figure => <tr key={`${figure.indicator}@${figure.year}`}>
        <td>{indicatorLabel(figure.indicator)}</td><td>{figure.year}</td><td className="num">{formatAmount(figure.value, figure.unit)}</td>
        <td className="ws-mono ws-muted">{figure.formula}</td></tr>)}</tbody></table></div>
  </section>;
}

/** When the draft was written; the run's technical details stay one hover away. */
function RunSummary({ generation }: { generation: Generation }) {
  const usage = generation.result?.generation?.usage;
  const na = w("common.notAvailable");
  const duration = runDuration(generation) || na;
  const details = w("draft.runDetails", { model: generation.model || na,
    seconds: duration,
    tokens: usage?.total_tokens ?? na, prompt: generation.prompt_version || na });
  return <p className="ws-small ws-muted" style={{ margin: 0 }} title={details}>
    {w("draft.generatedBy", { date: formatDateTime(generation.finished_at || generation.created_at) })}</p>;
}

/** Gaps the server words in English for its logs, said in the reader's language. */
export function gapText(gap: string): string {
  const fact = /^Missing source fact: (\w+)@(N(?:-1)?)$/.exec(gap);
  if (fact) return w("gap.missingFact", { indicator: fact[1].replace(/_/g, " "), year: fact[2] });
  if (gap.startsWith("No source available for this section")) return w("gap.noSource");
  return gap;
}

function legacyFigureTable(table: FigureTableData): SFCRTable {
  const columns: SFCRTableColumn[] = [{ key: "indicator", label: w("table.indicator"), type: "text" }];
  if (table.has_prior) columns.push({ key: "previousValue", label: "N-1", type: "number" });
  columns.push({ key: "currentValue", label: "N", type: "number" });
  if (table.has_variation || table.rows.some(row => row.delta != null)) columns.push({ key: "variation", label: w("table.delta"), type: "number" });
  return { id: "legacy-figures", columns, rows: table.rows.map(row => {
    const variationType = row.variation != null ? "percent" : row.delta_unit === "pts" ? "percentage_point" : row.delta != null ? "absolute" : null;
    return {
      indicator: indicatorLabel(row.indicator),
      indicatorKey: row.indicator,
      unit: row.unit,
      previousValue: table.has_prior ? row.n1 : null,
      currentValue: row.n,
      variation: row.variation ?? row.delta,
      variationType,
      sourceRefs: [row.source_n, row.source_n1, row.datapoint || (row.formula ? w("table.computed") : null)].filter(Boolean) as string[],
    };
  }) };
}

export function figureTables(tables?: SFCRTable[] | null, legacy?: FigureTableData | null): SFCRTable[] {
  const available = (tables || []).filter(table => Array.isArray(table.rows) && table.rows.length > 0);
  if (available.length) return available;
  return legacy && legacy.rows.length ? [legacyFigureTable(legacy)] : [];
}

/** One cell of an evidence table, compared at a glance: amounts rounded (exact on
 * hover), the change signed and coloured, % for amounts and points for ratios. */
function formatCell(value: SFCRTableRow[keyof SFCRTableRow], column: SFCRTableColumn, row: SFCRTableRow): ReactNode {
  if (value == null || value === "") return <span className="ws-faint">—</span>;
  if (column.key === "indicator") return String(value);
  if ((column.key === "variation" || column.key === "bridgeValue") && typeof value === "number") {
    const sign = value > 0 ? "+" : "";
    const text = row.variationType === "percent" ? `${sign}${formatAmount(value, "%")}`
      : row.variationType === "percentage_point" ? `${sign}${formatAmount(value, "pts")}`
      : `${sign}${formatCompact(value, row.unit)}`;
    return <span className={value > 0 ? "ws-up" : value < 0 ? "ws-down" : undefined}
      title={row.variationType === "absolute" ? `${sign}${formatAmount(value, row.unit)}` : undefined}>{text}</span>;
  }
  if (typeof value === "number") return <span title={formatAmount(value, row.unit)}>{formatCompact(value, row.unit)}</span>;
  return String(value);
}

/** One figure per row, N-1 before N, and their change: the year-on-year comparison
 * a section describes. Each cell's exact value and source show on hover. */
export function CompareTable({ rows }: { rows: { key: string; label: ReactNode; hint?: string | null; unit: string;
                                                  n: number | null; n1: number | null; delta: number | null;
                                                  deltaUnit: string | null; variation: number | null;
                                                  sourceN?: string | null; sourceN1?: string | null; extra?: ReactNode }[] }) {
  const value = (amount: number | null, unit: string, source?: string | null) => amount == null
    ? <span className="ws-faint">—</span>
    : <span title={[formatAmount(amount, unit), source].filter(Boolean).join(" · ")}>{formatCompact(amount, unit)}</span>;
  const extra = rows.some(row => row.extra);
  return <table className="ws-table ws-compare">
    <thead><tr><th>{w("table.indicator")}</th><th className="num">N-1</th><th className="num">N</th>
      <th className="num">{w("table.var")}</th>{extra && <th />}</tr></thead>
    <tbody>{rows.map(row => <tr key={row.key}>
      <td title={row.hint || undefined}>{row.label}</td>
      <td className="num">{value(row.n1, row.unit, row.sourceN1)}</td>
      <td className="num">{value(row.n, row.unit, row.sourceN)}</td>
      <td className="num"><Variation delta={row.delta} deltaUnit={row.deltaUnit} variation={row.variation} /></td>
      {extra && <td className="ws-small">{row.extra}</td>}
    </tr>)}</tbody>
  </table>;
}

/** Evidence-driven section tables (Indicateur | N-1 | N | Var.), rendered only
 * when rows exist; a row's sources show on its indicator, the table's below it. */
export function FigureTable({ table }: { table: SFCRTable | FigureTableData }) {
  const normalized = "columns" in table ? table : legacyFigureTable(table);
  if (!normalized.rows.length) return null;
  const numeric = (column: SFCRTableColumn) => column.key !== "indicator";
  const label = (column: SFCRTableColumn) => column.key === "variation" ? w("table.var") : column.label;
  return <div className="ws-table-wrap ws-figure-table">
    <table className="ws-table ws-compare">
      {normalized.title && <caption className="ws-sr-only">{normalized.title}</caption>}
      <thead><tr>{normalized.columns.map(column =>
        <th key={column.key} className={numeric(column) ? "num" : undefined}>{label(column)}</th>)}</tr></thead>
      <tbody>{normalized.rows.map((row, index) => <tr key={row.indicatorKey || row.indicator || index}>
        {normalized.columns.map(column => {
          const value = row[column.key as keyof SFCRTableRow];
          return <td key={column.key} className={numeric(column) ? "num" : undefined}
            title={column.key === "indicator" && row.sourceRefs?.length ? row.sourceRefs.join(" · ") : undefined}>
            {formatCell(value, column, row)}
            {column.key === "indicator" && row.validationStatus && row.validationStatus !== "verified_current" &&
              <div className="ws-small" style={{ color: row.validationStatus === "conflicting_sources" ? "#a12626" : "#665015" }}>
                {w(`table.${row.validationStatus}`)}</div>}
            {column.key === "indicator" && row.provenance && <details className="ws-small">
              <summary>{w("table.provenance")}</summary>
              {(["current", "previous", "gaap"] as const).map(period => {
                const fact = row.provenance?.[period];
                if (!fact) return null;
                return <div key={period} style={{ whiteSpace: "normal", overflowWrap: "anywhere" }}>
                  <strong>{row.provenanceLabels?.[period] || (period === "current" ? "N" : period === "previous" ? "N-1" : "French GAAP")}</strong>
                  <p>{[fact.entity, fact.reporting_period, fact.scenario, fact.gross_net, fact.valuation_basis,
                    fact.raw_value, fact.unit].filter(value => value != null).map(String).join(" · ")}</p>
                  <p>{String(fact.evidence || "")}</p>
                  {fact.formula ? <p>{String(fact.formula)}</p> : null}
                  {Array.isArray(fact.source_cells) && fact.source_cells.map((cell, index) => <div key={index}>
                    <p>{[cell.sheet, cell.cell, cell.template, cell.row_code, cell.column_code, cell.raw_value, cell.raw_unit]
                      .filter(value => value != null).map(String).join(" · ")}</p>
                    {cell.formula ? <p>{cell.formula} · {(cell.input_refs || []).join(", ")}</p> : null}
                  </div>)}
                  {Array.isArray(fact.conflict_sources) && fact.conflict_sources.map((source, index) =>
                    <p key={index}><strong>{w("table.conflicting_sources")}: </strong>{String(source.evidence || "")}</p>)}
                </div>;
              })}
            </details>}</td>;
        })}
      </tr>)}</tbody>
    </table>
    {normalized.sourceRefs?.length ? <p className="ws-small ws-faint" style={{ margin: "6px 0 0" }}>{normalized.sourceRefs.join(" · ")}</p>
      : <p className="ws-small ws-faint" style={{ margin: "6px 0 0" }}>{w("table.hoverNote")}</p>}
  </div>;
}

export const REVIEW_STATUS: Record<ReviewStatus, UserStatus> = {
  none: "not_started", ai_draft: "generated", edited: "generated", changes_requested: "needs_review", approved: "approved",
};

export function ReviewBadge({ status }: { status: ReviewStatus }) {
  return <StatusBadge status={REVIEW_STATUS[status]} label={w(`review.status.${status}`)} />;
}

export function useReview(code: string) {
  const { dossier } = useDossier();
  return useResource(keys.review(dossier.id, code), () => api.sectionReview(dossier.id, code));
}

/** What the section shows now: the AI draft, or the human revision that followed it (same citations). */
function shownResult(result: BlockResult | undefined, review: SectionReviewState | undefined): BlockResult | undefined {
  const current = review?.current;
  if (!result || !current || current.kind !== "revision") return result;
  return { ...result, draft_text: current.text, citations: current.citations, audit_findings: current.findings };
}

type Decision = "approved" | "changes_requested";

type StepState = "done" | "ko" | "warning" | "skipped" | "pending" | "running";
const STEP_STATUS: Record<StepState, UserStatus> = { done: "ready", ko: "failed", warning: "needs_review", skipped: "not_started",
  pending: "not_started", running: "processing" };
const PRE_CHECKS = ["tp_reconciliation", "balance_sheet", "solvency"];
const FIGURE_CHECKS = ["unsupported_figure", "citation_mismatch"];

/** The block's production chain, step by step, from what the last run actually did. */
function ChainCard({ code, latest, review }: { code: string; latest: Generation | undefined; review: SectionReviewState | undefined }) {
  const { dossier } = useDossier();
  const requests = useResource(keys.requests(dossier.id, code), () => api.sectionRequests(dossier.id, code));
  const [open, setOpen] = useState(false);
  const result = completedResult(latest);
  const running = !!latest && ACTIVE.includes(latest.status);
  const facts = Object.keys(latest?.inputs?.facts || {}).length;
  const missing = latest?.inputs?.missing?.length || 0;
  const passages = latest?.inputs?.passages?.length || 0;
  const answers = (latest?.inputs?.passages || []).filter(p => p.id.startsWith("req:")).length;
  const derived = result?.derived_figures.length || 0;
  const findings = review?.current?.findings || result?.audit_findings || [];
  const of = (checks: string[]) => findings.filter(f => checks.includes(f.check));
  const regulatory = findings.filter(f => !PRE_CHECKS.includes(f.check) && !FIGURE_CHECKS.includes(f.check) && f.check !== "citations_removed");
  const drafted = !!result?.draft_text;
  const escalated = latest?.block_status === "escalated";
  const failed = latest?.status === "failed";
  const state = (list: AuditFindingItem[]): StepState => list.some(f => f.severity === "blocking") ? "ko" : list.length ? "warning" : "done";
  const items = requests.data?.items || [];
  const steps: { key: string; state: StepState; detail: string; human?: boolean }[] = !latest ? [] : [
    { key: "extract", state: running ? "running" : facts ? (missing ? "warning" : "done") : "skipped",
      detail: facts ? w("chain.extractDetail", { facts, missing }) : w("chain.noFacts") },
    { key: "compute", state: running ? "pending" : derived ? "done" : "skipped", detail: w("chain.computeDetail", { count: derived }) },
    { key: "prechecks", state: running ? "pending" : of(PRE_CHECKS).length ? state(of(PRE_CHECKS)) : facts ? "done" : "skipped",
      detail: of(PRE_CHECKS).map(f => f.message).join(" ") || w("chain.noFinding") },
    { key: "sources", state: running ? "running" : passages ? "done" : "skipped",
      detail: w("chain.sourcesDetail", { count: passages, answers }) },
    { key: "draft", state: running ? "running" : failed ? "ko" : escalated ? "warning" : drafted ? "done" : "skipped",
      detail: failed ? (latest.error_message || "") : escalated ? w("chain.escalated") : drafted ? w("chain.draftDetail", { model: latest.model || "?" }) : "" },
    { key: "citations", state: !drafted ? "skipped" : "done", detail: w("chain.citationsDetail", { count: result?.citations.length || 0 }) },
    { key: "figures", state: !drafted ? "skipped" : state(of(FIGURE_CHECKS)), detail: of(FIGURE_CHECKS).map(f => f.message).join(" ") || w("chain.noFinding") },
    { key: "regulatory", state: !drafted ? "skipped" : state(regulatory), detail: regulatory.map(f => f.message).join(" ") || w("chain.noFinding") },
    { key: "escalation", human: true, state: items.some(i => i.status === "open") ? "pending" : items.length ? "done" : (result?.gaps.length ? "warning" : "skipped"),
      detail: items.length ? w("chain.requestsDetail", { open: items.filter(i => i.status === "open").length, answered: items.filter(i => i.status === "answered").length })
        : result?.gaps.length ? w("chain.gapsDetail", { count: result.gaps.length }) : w("chain.noGap") },
    { key: "review", human: true, state: review?.status === "approved" ? "done" : review?.status === "changes_requested" ? "warning" : drafted ? "pending" : "skipped",
      detail: w(`review.status.${review?.status || "none"}`) },
  ];
  if (!latest) return null;
  const done = steps.filter(s => s.state === "done").length;
  return <section className="ws-card">
    <button className="ws-card-head ws-chain-toggle" aria-expanded={open} onClick={() => setOpen(value => !value)}>
      <h2>{w("chain.title")}</h2><span className="ws-small ws-muted">{w("chain.summary", { done, total: steps.length })}</span></button>
    {open && <ol className="ws-chain">{steps.map(step => <li key={step.key} className={`ws-chain-step ${step.state}`}>
      <div className="ws-row" style={{ justifyContent: "space-between", gap: 8 }}>
        <strong>{w(`chain.step.${step.key}`)}{step.human && <span className="ws-tag" style={{ marginLeft: 6 }}>{w("chain.human")}</span>}</strong>
        <StatusBadge status={STEP_STATUS[step.state]} label={w(`chain.state.${step.state}`)} /></div>
      {step.detail && <div className="ws-small ws-muted" style={{ marginTop: 2 }}>{step.detail}</div>}
    </li>)}</ol>}
  </section>;
}

const REQUEST_STATUS: Record<ContributorRequestItem["status"], UserStatus> = { open: "processing", answered: "ready", cancelled: "not_started" };

/** Ask a contributor (by default the Responsible of the chapter) for a missing item. */
function RequestDialog({ code, gap, onClose }: { code: string; gap: string; onClose: () => void }) {
  const { dossier } = useDossier();
  const requests = useResource(keys.requests(dossier.id, code), () => api.sectionRequests(dossier.id, code));
  const team = useResource(keys.team(dossier.id), () => api.team(dossier.id));
  const create = useMutation(api.createRequest);
  const [question, setQuestion] = useState(gap ? w("request.template", { code, gap }) : "");
  const [contributor, setContributor] = useState<string>("");
  const [created, setCreated] = useState<{ link: string; email: string; name: string; emailed: boolean; configured: boolean } | null>(null);
  const [copied, setCopied] = useState(false);
  const responsible = requests.data?.responsible;
  const chosen = contributor || responsible?.id || "";
  const active = (team.data?.contributors || []).filter(c => c.active);
  const send = async () => {
    const result = await create.run(dossier.id, code, { question: question.trim(), gap: gap || null, contributor_id: chosen || null });
    if (result) {
      setCreated({ link: `${window.location.origin}${result.link_path}`, email: result.request.contributor?.email || "",
        name: result.request.contributor?.name || "", emailed: result.emailed, configured: result.mail_configured });
      invalidate(keys.requests(dossier.id, code), keys.audit(dossier.id));
    }
  };
  const mailto = created ? `mailto:${created.email}?subject=${encodeURIComponent(w("request.mailSubject", { code, entity: dossier.entity_name }))}`
    + `&body=${encodeURIComponent(w("request.mailBody", { name: created.name, question: question.trim(), link: created.link }))}` : "";
  return <Dialog title={w("request.title", { code })} onClose={onClose} size="md"
    footer={created ? <button className="ws-btn primary" onClick={onClose}>{w("common.close")}</button>
      : <><button className="ws-btn" onClick={onClose}>{w("common.cancel")}</button>
        <button className="ws-btn primary" disabled={create.pending || !question.trim() || !chosen} onClick={send}><Send size={16} />{w("request.create")}</button></>}>
    {!created && <>
      <div className="ws-field">
        <label htmlFor="req-contributor">{w("request.to")}</label>
        <select id="req-contributor" className="ws-select" value={chosen} onChange={e => setContributor(e.target.value)}>
          <option value="">{w("request.choose")}</option>
          {active.map(c => <option key={c.id} value={c.id}>{c.name}{c.id === responsible?.id ? ` (${w("request.responsible")})` : ""}</option>)}</select>
        {!active.length && <span className="ws-small ws-muted">{w("request.noTeam")}</span>}
      </div>
      <div className="ws-field">
        <label htmlFor="req-question">{w("request.question")}</label>
        <textarea id="req-question" className="ws-textarea" rows={5} value={question} onChange={e => setQuestion(e.target.value)} data-autofocus />
      </div>
      {create.error && <ErrorState title={w("request.createError")} error={create.error} />}
    </>}
    {created && <>
      <Notice tone="positive" title={w("request.createdTitle", { name: created.name })}>
        {created.emailed ? w("request.emailed", { email: created.email }) : w("request.createdBody")}</Notice>
      {created.configured && !created.emailed && <div style={{ marginTop: 8 }}><Notice tone="caution">{w("request.emailFailed")}</Notice></div>}
      <div className="ws-field" style={{ marginTop: 12 }}>
        <label htmlFor="req-link">{w("request.link")}</label>
        <input id="req-link" className="ws-input" readOnly value={created.link} onFocus={e => e.target.select()} />
      </div>
      <div className="ws-row">
        <button className="ws-btn" onClick={async () => { try { await navigator.clipboard.writeText(created.link); setCopied(true); } catch { setCopied(false); } }}>
          <Copy size={16} />{copied ? w("request.copied") : w("request.copy")}</button>
        <a className="ws-btn" href={mailto}><Mail size={16} />{w("request.mail")}</a>
      </div>
    </>}
  </Dialog>;
}

function RequestsCard({ code, onAsk }: { code: string; onAsk: () => void }) {
  const { dossier } = useDossier();
  const requests = useResource(keys.requests(dossier.id, code), () => api.sectionRequests(dossier.id, code));
  const cancel = useMutation(api.cancelRequest);
  const items = requests.data?.items || [];
  return <section className="ws-card">
    <div className="ws-card-head"><h2>{w("request.listTitle")}</h2>
      <button className="ws-btn sm" onClick={onAsk}><Send size={14} />{w("request.new")}</button></div>
    {!items.length && <p className="ws-card-pad ws-muted ws-small" style={{ margin: 0 }}>{w("request.none")}</p>}
    <ul className="ws-list">{items.map(item => <li key={item.id} style={{ alignItems: "flex-start" }}>
      <span className="ws-list-title"><strong>{item.question}</strong>
        <span className="ws-cell-sub">{w("request.meta", { name: item.contributor?.name || "?", date: formatDateTime(item.created_at) })}</span>
        {item.answer && <span className="ws-request-answer">{item.answer}</span>}</span>
      <span className="ws-row"><StatusBadge status={REQUEST_STATUS[item.status]} label={w(`request.status.${item.status}`)} />
        {item.status === "open" && <button className="ws-btn sm ghost" disabled={cancel.pending}
          onClick={async () => { if (await cancel.run(dossier.id, item.id)) invalidate(keys.requests(dossier.id, code), keys.audit(dossier.id)); }}>
          {w("request.cancel")}</button>}</span>
    </li>)}</ul>
    {items.some(i => i.status === "answered") && <p className="ws-card-pad ws-small ws-muted" style={{ margin: 0 }}>{w("request.answeredNote")}</p>}
  </section>;
}

function DecisionDialog({ code, decision, content, onClose, onDone }: {
  code: string; decision: Decision; content: SectionContent; onClose: () => void; onDone: () => void;
}) {
  const { dossier } = useDossier();
  const [comment, setComment] = useState("");
  const submit = useMutation(api.reviewSection);
  const blocking = content.findings.filter(f => f.severity === "blocking");
  const required = decision === "changes_requested" || blocking.length > 0;
  const send = async () => {
    if (await submit.run(dossier.id, code, decision, content.id, comment.trim())) onDone();
  };
  return <Dialog title={w(`review.dialog.${decision}`)} onClose={onClose} size="sm"
    footer={<><button className="ws-btn" onClick={onClose}>{w("common.cancel")}</button>
      <button className="ws-btn primary" disabled={submit.pending || (required && !comment.trim())}
        onClick={send}>{w(`review.action.${decision}`)}</button></>}>
    {decision === "approved" && blocking.length > 0 && <Notice tone="caution" title={w("review.blockingTitle", { count: blocking.length })}>
      <ul style={{ margin: 0, paddingLeft: 18 }}>{blocking.map((f, i) => <li key={i}>{f.message}</li>)}</ul></Notice>}
    <div className="ws-field" style={{ marginTop: 12 }}>
      <label htmlFor="review-comment">{w(required ? (decision === "approved" ? "review.justification" : "review.comment") : "review.commentOptional")}</label>
      <textarea id="review-comment" className="ws-textarea" rows={4} value={comment} onChange={e => setComment(e.target.value)} data-autofocus />
    </div>
    {submit.error && <ErrorState title={w("review.saveError")} error={submit.error} />}
  </Dialog>;
}

function Editor({ code, content, onClose }: { code: string; content: SectionContent; onClose: () => void }) {
  const { dossier } = useDossier();
  const [text, setText] = useState(content.text);
  const save = useMutation(api.saveDraft);
  const submit = async () => {
    if (await save.run(dossier.id, code, text, content.id)) {
      invalidate(keys.review(dossier.id, code), keys.reviews(dossier.id), keys.audit(dossier.id), `report-audits:${dossier.id}`,
        `report:${dossier.id}`, `comparison:${dossier.id}`, `schedule:${dossier.id}`);
      onClose();
    }
  };
  return <section className="ws-card">
    <div className="ws-card-head"><h2>{w("review.editTitle")}</h2><span className="ws-small ws-muted">{w("review.editHint", { count: content.citations.length })}</span></div>
    <div className="ws-card-pad">
      <label className="ws-sr-only" htmlFor="draft-editor">{w("review.editTitle")}</label>
      <textarea id="draft-editor" className="ws-textarea ws-editor" rows={16} value={text} onChange={e => setText(e.target.value)} />
      {save.error && <ErrorState title={w("review.saveError")} error={save.error}
        note={save.error.code === "CONTENT_CHANGED" ? w("review.changedNote") : undefined} />}
      <div className="ws-row" style={{ justifyContent: "flex-end", marginTop: 12 }}>
        <button className="ws-btn" onClick={onClose}>{w("common.cancel")}</button>
        <button className="ws-btn primary" disabled={save.pending || !text.trim() || text.trim() === content.text.trim()} onClick={submit}>
          {w("review.saveVersion")}</button>
      </div>
    </div>
  </section>;
}

function ReviewNotice({ review, onRestore, restoring }: { review: SectionReviewState; onRestore: () => void; restoring: boolean }) {
  const decision = review.decision;
  if (decision?.decision === "changes_requested") return <Notice tone="caution" title={w("review.changesTitle", { actor: decision.actor, date: formatDateTime(decision.created_at) })}>
    <p style={{ margin: 0 }}>{decision.comment}</p></Notice>;
  if (decision?.decision === "approved") return <Notice tone="positive" title={w("review.approvedTitle", { actor: decision.actor, date: formatDateTime(decision.created_at) })}>
    {review.carried_from && <p style={{ margin: 0 }}>{w("review.carried")}</p>}
    {decision.comment && <p style={{ margin: 0 }}>{decision.comment}</p>}
    {decision.open_findings.length > 0 && <p className="ws-small" style={{ margin: "4px 0 0" }}>{w("review.approvedWithFindings", { count: decision.open_findings.length })}</p>}</Notice>;
  if (review.last_approved && !review.approved_is_current) return <Notice tone="neutral">
    <p style={{ margin: 0 }}>{w("review.previousApproved", { actor: review.last_approved.actor, date: formatDateTime(review.last_approved.created_at) })}</p>
    <button className="ws-btn sm" style={{ marginTop: 8 }} disabled={restoring} onClick={onRestore}>{w("review.restore")}</button></Notice>;
  return null;
}

export function DraftTab({ code }: { code: string }) {
  const { dossier, readiness, openUpload, documents } = useDossier();
  // Drafting without the required inputs only reports what is missing: point to the documents first.
  const notReady = readiness.data?.chapters.flatMap(chapter => chapter.sections)
    .find(section => section.code === code)?.status === "NOT_READY";
  // Documents about another company are never a source: say so, it is the usual reason nothing is found.
  const foreign = (documents.data || []).filter(d => d.review_reasons.some(reason => reason.kind === "entity")).length;
  const allForeign = foreign > 0 && foreign === (documents.data || []).length;
  const unconfirmed = (documents.data || []).filter(d => d.review_reasons.some(reason => reason.kind === "entity_unknown")).length;
  const foreignNote = (foreign > 0 || unconfirmed > 0) && <>
    {foreign > 0 && <p style={{ margin: "8px 0 0" }}>{w(allForeign ? "draft.foreignAll" : "draft.foreignSome",
      { count: foreign, entity: dossier.entity_name })}</p>}
    {unconfirmed > 0 && <p style={{ margin: "8px 0 0" }}>{w("draft.unconfirmed", { count: unconfirmed })}</p>}
    <Link className="ws-btn sm" style={{ marginTop: 8 }} to={`/dossiers/${dossier.id}/documents`}>{w("next.checkAction")}</Link></>;
  const generations = useGenerations(code);
  const review = useReview(code);
  const [confirm, setConfirm] = useState(false);
  const [editing, setEditing] = useState(false);
  const [deciding, setDeciding] = useState<Decision | null>(null);
  const [asking, setAsking] = useState<string | null>(null);
  const { can } = useCurrentUser();
  const [layout, setLayout, narrow] = useDraftLayout();
  const side = layout === "side";
  const [selection, setSelection] = useState<Selection | null>(null);
  const start = useMutation(api.startGeneration);
  const restore = useMutation(api.restoreApproved);
  const latest = generations.data?.items[0];
  const running = !!latest && ACTIVE.includes(latest.status);
  const now = useNow(running);
  const currentRunDuration = latest ? runDuration(latest, now) : null;
  const result = shownResult(completedResult(latest), review.data);
  const content = review.data?.current;
  const status = review.data?.status || "none";
  const refreshReview = () => invalidate(keys.review(dossier.id, code), keys.reviews(dossier.id), keys.audit(dossier.id), `report-audits:${dossier.id}`,
    `report:${dossier.id}`, `comparison:${dossier.id}`, `schedule:${dossier.id}`);

  const generate = async () => {
    setConfirm(false);
    if (await start.run(dossier.id, code)) invalidate(keys.generations(dossier.id, code), keys.generationSummary(dossier.id), keys.audit(dossier.id));
  };
  useEffect(() => { if (!running) refreshReview(); }, [running]); // eslint-disable-line react-hooks/exhaustive-deps
  if (generations.loading) return <SkeletonBlock lines={6} />;
  if (generations.error) return <ErrorState title={w("draft.loadError")} error={generations.error} onRetry={generations.reload} />;
  if (!generations.data?.supported) return <Notice tone="neutral" title={w("draft.unsupportedTitle")}><p style={{ margin: 0 }}>{w("draft.unsupportedBody")}</p></Notice>;

  return <div className="ws-stack">
    <div className="ws-card ws-card-pad ws-row" style={{ justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
      <div className="ws-row">
        {/* Once there is a text to review, its review status is what counts; the run state shows only when it adds something. */}
        {(!content || running || latest?.status === "failed" || latest?.block_status === "audit_ko") &&
          <StatusBadge status={blockUserStatus(latest)} label={w(`draft.state.${latest ? (running ? "running" : latest.status === "failed" ? "failed" : latest.block_status) : "none"}`)} />}
        {content && <ReviewBadge status={status} />}
        {content?.kind === "revision" && <span className="ws-tag">{w("review.humanVersion", { n: content.version_number ?? 1 })}</span>}
      </div>
      <div className="ws-row">
        {content && !running && !editing && <>
          <button className="ws-btn" onClick={() => setEditing(true)}><Pencil size={16} />{w("review.action.edit")}</button>
          {status !== "changes_requested" && <button className="ws-btn" onClick={() => setDeciding("changes_requested")}
            disabled={!can("reviewer")} title={can("reviewer") ? undefined : w("auth.needs", { role: w("role.user.reviewer") })}>
            <MessageSquare size={16} />{w("review.action.changes_requested")}</button>}
          {status !== "approved" && <button className="ws-btn success" onClick={() => setDeciding("approved")}
            disabled={!can("reviewer")} title={can("reviewer") ? undefined : w("auth.needs", { role: w("role.user.reviewer") })}>
            <CheckCircle2 size={16} />{w("review.action.approved")}</button>}
        </>}
        {!result?.draft_text && notReady && !running &&
          <button className="ws-btn primary" onClick={() => openUpload()}><Plus size={16} />{w("docs.add")}</button>}
        {/* Once a draft exists, reviewing it is the next step: drafting again is secondary. */}
        <button className={`ws-btn ${result?.draft_text ? "ghost" : notReady ? "" : "primary"}`} disabled={running || start.pending}
          onClick={() => (result?.draft_text ? setConfirm(true) : generate())}>
          {running ? w("draft.generating") : result?.draft_text ? w("draft.regenerate") : w("draft.generate")}</button>
        {!narrow && <div className="ws-segmented" role="group" aria-label={w("draft.layout.label")}>
          <button className={side ? "active" : ""} aria-pressed={side} title={w("draft.layout.side")} onClick={() => { setSelection(null); setLayout("side"); }}>
            <PanelRight size={15} /><span className="ws-sr-only">{w("draft.layout.side")}</span></button>
          <button className={!side ? "active" : ""} aria-pressed={!side} title={w("draft.layout.stack")} onClick={() => { setSelection(null); setLayout("stack"); }}>
            <Rows3 size={15} /><span className="ws-sr-only">{w("draft.layout.stack")}</span></button>
        </div>}
      </div>
    </div>
    {start.error && <ErrorState title={w("draft.startError")} error={start.error} />}
    {review.error && <ErrorState title={w("review.loadError")} error={review.error} onRetry={review.reload} />}
    {!!review.data?.stale_sources?.length && <Notice tone="caution">{w("review.staleSources")}</Notice>}
    {running && <Notice tone="info">{w("draft.runningBody", { code, duration: currentRunDuration || "0 s" })}</Notice>}
    {latest?.status === "failed" && <ErrorState title={w("draft.failedTitle")} error={null}
      note={`${latest.error_message || ""}${latest.error_code === "AI_NOT_CONFIGURED" ? ` ${w("draft.aiNotConfigured")}` : ""}`}
      onRetry={generate} />}
    {latest?.block_status === "escalated" && !content && <Notice tone="caution"
      title={w(allForeign ? "draft.foreignTitle" : "draft.escalatedTitle")}>
      {!allForeign && <p style={{ margin: 0 }}>{w("draft.escalatedBody")}</p>}
      {foreignNote}
      {!notReady && !foreign && !unconfirmed && <button className="ws-btn sm" style={{ marginTop: 8 }} onClick={() => openUpload()}>
        <Plus size={14} />{w("docs.add")}</button>}</Notice>}
    {!latest && notReady && <Notice tone="caution" title={w(allForeign ? "draft.foreignTitle" : "draft.notReadyTitle")}>
      {!allForeign && <p style={{ margin: 0 }}>{w("draft.notReadyBody")}</p>}
      {foreignNote}</Notice>}
    {!latest && !notReady && <div className="ws-card"><EmptyState title={w("draft.emptyTitle")} body={w("draft.emptyBody")} /></div>}
    {review.data && <ReviewNotice review={review.data} restoring={restore.pending} onRestore={async () => {
      if (review.data?.current && await restore.run(dossier.id, code, review.data.current.id)) refreshReview();
    }} />}
    {restore.error && <ErrorState title={w("review.restoreError")} error={restore.error} />}
    {review.data && review.data.new_inputs.length > 0 && !running && <Notice tone="info" title={w("draft.newInputsTitle")}>
      <p style={{ margin: 0 }}>{w("draft.newInputsBody", { documents: review.data.new_inputs.map(d => d.name).join(", ") })}</p>
      <button className="ws-btn sm" style={{ marginTop: 8 }} disabled={start.pending}
        onClick={() => (result?.draft_text ? setConfirm(true) : generate())}>{w("draft.regenerate")}</button></Notice>}

    {(() => {
      const main = <>
    {editing && content && <Editor code={code} content={content} onClose={() => setEditing(false)} />}
    {!editing && result?.draft_text && <section className="ws-card">
      <div className="ws-card-head"><h2>{w("section.tab.draft")}</h2>
        {content?.kind === "revision"
          ? <span className="ws-small ws-muted">{w("review.revisionBy", { actor: content.created_by, date: formatDateTime(content.created_at) })}</span>
          : <RunSummary generation={latest!} />}</div>
      <div className="ws-card-pad">{figureTables(content?.tables, content?.table).map(table => <FigureTable key={table.id} table={table} />)}
        <DraftText text={result.draft_text} citations={result.citations} onSelect={setSelection} />
        <p className="ws-small ws-faint" style={{ marginBottom: 0 }}>{w("draft.selectHint")}</p></div>
    </section>}
      </>;
      const insights = <>
    {side && selection && result && <EvidencePanel inline selection={selection} result={result} facts={latest?.inputs?.facts || {}}
      onClose={() => setSelection(null)} />}
    {result?.draft_text && <FindingsCard findings={result.audit_findings} />}
    {result && result.gaps.length > 0 && <section className="ws-card">
      <div className="ws-card-head"><h2>{w("draft.missingInfo")}</h2><span className="ws-small ws-muted">{w("draft.missingInfoHint")}</span></div>
      <ul className="ws-list">{result.gaps.map(gap => <li key={gap}><span>{gapText(gap)}</span>
        <span className="ws-row"><StatusBadge status="missing" label={w("status.missing")} />
          <button className="ws-btn sm" onClick={() => setAsking(gap)}><Send size={14} />{w("request.ask")}</button></span></li>)}</ul>
    </section>}
    <RequestsCard code={code} onAsk={() => setAsking("")} />
    {result && <DerivedTable figures={result.derived_figures} />}
    <ChainCard code={code} latest={latest} review={review.data} />
      </>;
      return side
        ? <div className="ws-draft-layout"><div className="ws-draft-main ws-stack">{main}</div>
            <aside className="ws-draft-aside ws-stack" aria-label={w("draft.insights")}>
              <h2 className="ws-draft-aside-title">{w("draft.insights")}</h2>{insights}</aside></div>
        : <>{main}{insights}</>;
    })()}
    {asking !== null && <RequestDialog code={code} gap={asking} onClose={() => setAsking(null)} />}

    {confirm && <Dialog title={w("draft.confirmTitle", { code })} onClose={() => setConfirm(false)} size="sm"
      footer={<><button className="ws-btn" onClick={() => setConfirm(false)} data-autofocus>{w("common.cancel")}</button>
        <button className="ws-btn primary" onClick={generate}>{w("draft.regenerate")}</button></>}>
      <p style={{ marginTop: 0 }}>{w("draft.confirmBody")}</p>
      {(content?.kind === "revision" || status === "approved") && <p style={{ marginBottom: 0 }}>{w("review.regenerateNote")}</p>}</Dialog>}
    {deciding && content && <DecisionDialog code={code} decision={deciding} content={content} onClose={() => setDeciding(null)}
      onDone={() => { setDeciding(null); refreshReview(); }} />}
    {!side && selection && result && <EvidencePanel selection={selection} result={result} facts={latest?.inputs?.facts || {}} onClose={() => setSelection(null)} />}
  </div>;
}

// Checks every section runs, then the section-specific ones (finding key, label).
const COMMON_CHECKS: [string, string][] = [["", "check.citations"], ["unsupported_figure", "check.figures"],
  ["citation_mismatch", "check.markers"], ["style_length", "check.style"]];
const SECTION_CHECKS: Record<string, [string, string][]> = {
  "A.2": [["", "check.ratios"]],
  "A.3": [["art_295_breakdown", "check.art295"]],
  "D.2": [["tp_reconciliation", "check.reconciliation"], ["art_308b_disclosure", "check.art308b"]],
  "D.3": [["balance_sheet", "check.balanceSheet"]],
  "E.2": [["solvency", "check.solvency"]],
  "E.5": [["solvency", "check.solvency"]],
};

export function ValidationTab({ code }: { code: string }) {
  const generations = useGenerations(code);
  const review = useReview(code);
  const latest = generations.data?.items[0];
  const result = shownResult(completedResult(latest), review.data);
  if (generations.loading) return <SkeletonBlock lines={4} />;
  if (!generations.data?.supported) return <Notice tone="neutral" title={w("draft.unsupportedTitle")}><p style={{ margin: 0 }}>{w("draft.unsupportedBody")}</p></Notice>;
  if (!result) return <div className="ws-card"><EmptyState title={w("validation.emptyTitle")} body={w("validation.emptyBody")} /></div>;
  const failed = new Set(result.audit_findings.map(finding => finding.check));
  return <div className="ws-stack">
    {review.data?.current?.kind === "revision" && <Notice tone="neutral">{w("validation.onRevision", { n: review.data.current.version_number ?? 1 })}</Notice>}
    <section className="ws-card">
      <div className="ws-card-head"><h2>{w("validation.findings")}</h2></div>
      {!result.audit_findings.length && <p className="ws-card-pad ws-muted" style={{ margin: 0 }}>{w("validation.noFindings")}</p>}
      <ul className="ws-list">{result.audit_findings.map((finding, index) => <li key={index} style={{ alignItems: "flex-start" }}>
        <span className="ws-list-title"><strong>{finding.message}</strong>{finding.remediation && <span className="ws-cell-sub">{finding.remediation}</span>}</span>
        <StatusBadge status={finding.severity === "blocking" ? "failed" : "needs_review"} label={w(`validation.${finding.severity}`)} />
      </li>)}</ul>
    </section>
    <section className="ws-card">
      <div className="ws-card-head"><h2>{w("validation.checks")}</h2></div>
      <ul className="ws-list">{[...COMMON_CHECKS, ...(SECTION_CHECKS[code] || [])].map(([key, check]) => {
        const ko = !!key && failed.has(key);
        return <li key={check}><span>{w(check)}</span><StatusBadge status={ko ? "failed" : "ready"} label={ko ? w("validation.fail") : w("validation.pass")} /></li>;
      })}</ul>
      {(code === "D.2" || code === "A.3") && <p className="ws-card-pad ws-small ws-muted" style={{ margin: 0 }}>{w("validation.lexicalNote")}</p>}
    </section>
  </div>;
}

export function HistoryTab({ code }: { code: string }) {
  const generations = useGenerations(code);
  const review = useReview(code);
  if (generations.loading) return <SkeletonBlock lines={4} />;
  if (generations.error) return <ErrorState title={w("draft.loadError")} error={generations.error} onRetry={generations.reload} />;
  const items = generations.data?.items || [];
  const reviews = review.data?.history || [];
  if (!items.length && !reviews.length) return <div className="ws-card"><EmptyState title={w("draft.historyEmpty")} /></div>;
  return <div className="ws-stack">
    {reviews.length > 0 && <section className="ws-card">
      <div className="ws-card-head"><h2>{w("review.historyTitle")}</h2></div>
      <ul className="ws-timeline">{reviews.map(item => <li key={item.id}>
        <time dateTime={item.created_at}>{formatDateTime(item.created_at)}</time>
        {item.type === "revision"
          ? <div><strong>{w("review.history.revision", { n: item.version_number })}</strong>
              <div className="ws-small ws-muted">{item.actor}{item.blocking ? ` · ${w("review.history.blocking", { count: item.blocking })}` : ""}</div></div>
          : <div><div className="ws-row" style={{ justifyContent: "space-between" }}>
              <strong>{w(`review.history.${item.decision}`)}</strong><ReviewBadge status={item.decision} /></div>
              <div className="ws-small ws-muted">{item.actor} · {item.content_kind === "revision" ? w("review.onRevision") : w("review.onDraft")}
                {item.comment ? ` · ${item.comment}` : ""}</div></div>}
      </li>)}</ul>
    </section>}
    {items.length > 0 && <section className="ws-card">
      <div className="ws-card-head"><h2>{w("review.runsTitle")}</h2></div>
      <ul className="ws-timeline">{items.map(item => <li key={item.id}>
        <time dateTime={item.created_at}>{formatDateTime(item.created_at)}</time>
        <div><div className="ws-row" style={{ justifyContent: "space-between" }}>
          <strong>{w("draft.runTitle", { code: item.section_code })}</strong><StatusBadge status={blockUserStatus(item)}
            label={w(`draft.state.${ACTIVE.includes(item.status) ? "running" : item.status === "failed" ? "failed" : item.block_status}`)} /></div>
          <div className="ws-small ws-muted">{item.model || w("common.notAvailable")}{item.prompt_version && ` · ${item.prompt_version}`}
            {runDuration(item) && ` · ${runDuration(item)}`} · {item.requested_by}
            {item.error_message && ` · ${item.error_message}`}</div></div>
      </li>)}</ul>
    </section>}
  </div>;
}
